<template>
  <oxd-dialog
    v-if="show"
    class="my-payslip-details-dialog"
    :style="dialogSheetStyle"
    :gutters="!isCompactViewport"
    @update:show="onClose"
  >
    <div class="my-payslip-details">
      <oxd-text type="card-title" class="my-payslip-details__title">
        Payslip Calculation Details
      </oxd-text>
      <oxd-divider />
      <oxd-form :loading="isLoading">
        <template v-if="!isLoading">
          <oxd-form-row class="payslip-details-fields-row">
            <payslip-label-value-table :rows="summaryRows" />
          </oxd-form-row>

          <oxd-form-row class="payslip-details-fields-row">
            <oxd-text tag="p" class="title-payslip-section">
              Salary Calculation Summary
            </oxd-text>
            <payslip-label-value-table :rows="salaryCalculationRows" />
          </oxd-form-row>

          <oxd-form-row class="payslip-details-fields-row">
            <oxd-text tag="p" class="title-payslip-section">
              Work Day Parameters
            </oxd-text>
            <payslip-label-value-table :rows="workDayParameterRows" />
          </oxd-form-row>

          <oxd-form-row class="payslip-details-fields-row">
            <oxd-text tag="p" class="title-payslip-section">
              Employee Payable Days
            </oxd-text>
            <payslip-label-value-table :rows="employeePayableDayRows" />
          </oxd-form-row>

          <oxd-form-row class="payslip-details-fields-row">
            <oxd-text tag="p" class="title-payslip-section">
              Employee Attendance Details
            </oxd-text>
            <payslip-label-value-table :rows="attendanceDetailRows" />
          </oxd-form-row>

          <oxd-form-row class="attendance-row">
            <oxd-text
              tag="p"
              class="title-payslip-section attendance-section-title"
            >
              <template v-if="attendanceFromDate && attendanceToDate">
                Attendance {{ attendanceFromDate }} – {{ attendanceToDate }}
                <span
                  v-if="attendanceRecords.length"
                  class="attendance-record-count"
                >
                  · {{ attendanceRecords.length }} records
                </span>
              </template>
              <template v-else>Attendance</template>
            </oxd-text>
            <div class="orangehrm-container">
              <payslip-attendance-breakdown-table
                :items="attendanceRecords"
                :loading="isLoading"
              />
              <oxd-text tag="p" class="attendance-legend-note">
                Note: WF = Work Fraction, LF = Leave Fraction, HF = Holiday
                Fraction, PF = Payable Fraction, PH = Public Holiday, leave
                codes = configured type abbreviation (or initials from the leave
                type name), OW = Outside Work Week.
              </oxd-text>
            </div>
          </oxd-form-row>

          <div class="payslip-items-tables">
            <oxd-form-row class="payslip-items-row">
              <oxd-text tag="p" class="title-payslip-section">
                Payslip Earnings
              </oxd-text>
              <payslip-items-table
                :items="earningItems"
                :loading="isLoading"
                :empty-message="$t('general.no_records_found')"
              />
            </oxd-form-row>

            <oxd-form-row class="payslip-items-row">
              <oxd-text tag="p" class="title-payslip-section">
                Payslip Deductions
              </oxd-text>
              <payslip-items-table
                :items="deductionItems"
                :loading="isLoading"
                :empty-message="$t('general.no_records_found')"
              />
            </oxd-form-row>
          </div>
        </template>
      </oxd-form>

      <oxd-divider />
      <oxd-form-actions>
        <oxd-button
          display-type="ghost"
          :label="$t('general.close')"
          @click="onClose"
        />
      </oxd-form-actions>
    </div>
  </oxd-dialog>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {OxdDialog} from '@ohrm/oxd';
import moment from 'moment';
import PayslipAttendanceBreakdownTable from '@/orangehrmPayrollPlugin/components/PayslipAttendanceBreakdownTable.vue';
import PayslipItemsTable from '@/orangehrmPayrollPlugin/components/PayslipItemsTable.vue';
import PayslipLabelValueTable from '@/orangehrmPayrollPlugin/components/PayslipLabelValueTable.vue';
import {DAILY_RATE_FORMULA_HINT} from '@/orangehrmPayrollPlugin/util/payrollFormulas';

const emptyCalculation = () => ({
  basicSalaryMonthly: '',
  basicSalary: '',
  grossSalary: '',
  scheduledWorkDays: '',
  publicHolidays: '',
  leaveFraction: '',
  holidayFraction: '',
  totalCalculationDays: '',
  payableDays: '',
  workingDays: '',
  lateMinutesTotal: '',
  earlyDepartures: '',
  absenceDays: '',
  taxableIncome: '',
});

export default {
  name: 'MyPayslipDetailsDialog',
  components: {
    'oxd-dialog': OxdDialog,
    'payslip-attendance-breakdown-table': PayslipAttendanceBreakdownTable,
    'payslip-items-table': PayslipItemsTable,
    'payslip-label-value-table': PayslipLabelValueTable,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    payslipId: {
      type: Number,
      default: null,
    },
    payrollRunName: {
      type: String,
      default: '',
    },
    timePeriod: {
      type: String,
      default: '',
    },
    apiResource: {
      type: String,
      default: 'my-payslips',
      validator: (value) => ['my-payslips', 'payslips'].includes(value),
    },
  },
  emits: ['close'],
  data() {
    return {
      isLoading: false,
      meta: {
        payrollRunName: '',
        timePeriod: '',
        status: '',
        grossAmount: '',
        totalDeductions: '',
        netAmount: '',
      },
      calculation: emptyCalculation(),
      attendanceFromDate: '',
      attendanceToDate: '',
      attendanceRecords: [],
      items: [],
      pageScrollY: 0,
      isCompactViewport: false,
      compactViewportQuery: null,
    };
  },
  computed: {
    dialogSheetStyle() {
      if (this.isCompactViewport) {
        return {
          width: '100%',
          maxWidth: '100%',
          minWidth: '0',
          margin: '0',
          borderRadius: '0',
          minHeight: '100dvh',
          height: 'auto',
          position: 'relative',
          boxSizing: 'border-box',
        };
      }
      return {
        width: '95%',
        maxWidth: '1400px',
      };
    },
    summaryRows() {
      return [
        {
          key: 'payrollRunName',
          label: this.$t('payroll.payroll_run_name'),
          value: this.meta.payrollRunName,
        },
        {
          key: 'timePeriod',
          label: this.$t('payroll.time_period'),
          value: this.meta.timePeriod,
        },
        {
          key: 'status',
          label: this.$t('general.status'),
          value: this.meta.status,
          capitalize: true,
        },
        {
          key: 'netAmount',
          label: this.$t('payroll.net_amount'),
          value: this.meta.netAmount,
        },
      ];
    },
    workDayParameterRows() {
      return [
        {
          key: 'scheduledWorkDays',
          label: 'Scheduled Work Days',
          value: this.calculation.scheduledWorkDays,
        },
        {
          key: 'publicHolidays',
          label: 'Holidays',
          value: this.calculation.publicHolidays,
        },
        {
          key: 'totalCalculationDays',
          label: 'Maximum Payable Days',
          value: this.calculation.totalCalculationDays,
        },
      ];
    },
    employeePayableDayRows() {
      return [
        {
          key: 'workingDays',
          label: 'Work Days',
          value: this.calculation.workingDays,
        },
        {
          key: 'absenceDays',
          label: 'Unpaid Leave / Absence Days',
          value: this.calculation.absenceDays,
        },
        {
          key: 'leaveFraction',
          label: 'Paid Leave Days',
          value: this.calculation.leaveFraction,
        },
        {
          key: 'holidayFraction',
          label: 'Public Holidays',
          value: this.calculation.holidayFraction,
        },
        {
          key: 'payableDays',
          label: 'Total Payable Days',
          value: this.calculation.payableDays,
        },
      ];
    },
    attendanceDetailRows() {
      return [
        {
          key: 'workingDays',
          label: 'Attended Days (WF)',
          value: this.calculation.workingDays,
        },
        {
          key: 'lateMinutesTotal',
          label: 'Late Minutes (Total)',
          value: this.calculation.lateMinutesTotal,
        },
        {
          key: 'earlyDepartures',
          label: 'Early Departures',
          value: this.calculation.earlyDepartures,
        },
      ];
    },
    salaryCalculationRows() {
      return [
        {
          key: 'basicSalaryMonthly',
          label: 'Basic Salary',
          value: this.calculation.basicSalaryMonthly,
        },
        {
          key: 'basicSalary',
          label: 'Daily Rate',
          value: this.calculation.basicSalary,
          hint: DAILY_RATE_FORMULA_HINT,
        },
        {
          key: 'grossSalary',
          label: 'Gross Salary',
          value: this.calculation.grossSalary || this.meta.grossAmount,
        },
        {
          key: 'totalDeductions',
          label: 'Total Deductions',
          value: this.meta.totalDeductions,
        },
        {
          key: 'taxableIncome',
          label: 'Taxable Income',
          value: this.calculation.taxableIncome,
        },
      ];
    },
    earningItems() {
      return (this.items || []).filter((item) => this.isEarningItem(item));
    },
    deductionItems() {
      return (this.items || []).filter((item) => this.isDeductionItem(item));
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(visible) {
        this.setPageScrollLocked(visible);
        if (visible && this.payslipId) {
          this.loadDetails();
        }
      },
    },
    payslipId(visible) {
      if (this.show && visible) {
        this.loadDetails();
      }
    },
  },
  mounted() {
    this.compactViewportQuery = window.matchMedia('(max-width: 767px)');
    this.isCompactViewport = this.compactViewportQuery.matches;
    this.onCompactViewportChange = (event) => {
      this.isCompactViewport = event.matches;
    };
    this.compactViewportQuery.addEventListener(
      'change',
      this.onCompactViewportChange,
    );
  },
  beforeUnmount() {
    this.setPageScrollLocked(false);
    this.compactViewportQuery?.removeEventListener(
      'change',
      this.onCompactViewportChange,
    );
  },
  methods: {
    setPageScrollLocked(locked) {
      const html = document.documentElement;
      const body = document.body;
      const layout = document.querySelector('.oxd-layout-container');

      if (locked) {
        this.pageScrollY = window.scrollY;
        html.style.overflow = 'hidden';
        html.style.overflowX = 'hidden';
        body.style.overflow = 'hidden';
        body.style.overflowX = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${this.pageScrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.classList.add('my-payslip-details-dialog-open');
        if (layout instanceof HTMLElement) {
          layout.style.overflow = 'hidden';
        }
        return;
      }

      html.style.overflow = '';
      html.style.overflowX = '';
      body.style.overflow = '';
      body.style.overflowX = '';
      body.classList.remove('my-payslip-details-dialog-open');
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      if (layout instanceof HTMLElement) {
        layout.style.overflow = '';
      }
      window.scrollTo(0, this.pageScrollY);
      this.pageScrollY = 0;
    },
    onClose() {
      this.$emit('close');
    },
    itemTypeParam(item) {
      const type = item?.itemType;
      if (typeof type === 'string') {
        return type;
      }
      return type?.param ?? '';
    },
    isEarningItem(item) {
      return this.itemTypeParam(item) === 'earning';
    },
    isDeductionItem(item) {
      return this.itemTypeParam(item) === 'deduction';
    },
    async loadDetails() {
      this.isLoading = true;
      try {
        const apiBase = `/api/v2/payroll/${this.apiResource}/${this.payslipId}`;
        const payslipHttp = new APIService(window.appGlobal.baseUrl, apiBase);
        const breakdownHttp = new APIService(
          window.appGlobal.baseUrl,
          `${apiBase}/attendance-breakdown`,
        );
        const [payslipRes, breakdownRes] = await Promise.all([
          payslipHttp.getAll(),
          breakdownHttp.getAll(),
        ]);
        const data = payslipRes?.data?.data ?? {};
        const breakdown = breakdownRes?.data?.data ?? {};
        this.applyPayslipMeta(data);
        this.applyBreakdown(breakdown);
        const items = data.payslipItems || [];
        this.items = items.map((item) => ({
          ...item,
          amount: this.formatCalculationValue(item.amount),
        }));
      } catch (e) {
        this.$toast?.error?.({
          title: this.$t('general.error'),
          message:
            e?.response?.data?.error?.message ||
            this.$t('general.unexpected_error'),
        });
        this.onClose();
      } finally {
        this.isLoading = false;
      }
    },
    applyPayslipMeta(data) {
      this.meta = {
        payrollRunName: this.payrollRunName || '',
        timePeriod: this.timePeriod || '',
        status: data.status || '',
        grossAmount: this.formatCalculationValue(data.grossAmount),
        totalDeductions: this.formatCalculationValue(data.totalDeductions),
        netAmount: this.formatCalculationValue(data.netAmount),
      };
    },
    applyBreakdown(breakdown) {
      const rows = breakdown.rows ?? [];
      const totals = breakdown.totals ?? {};
      const summary = breakdown.summary ?? {};
      if (rows.length > 0) {
        this.attendanceFromDate = rows[0].date || '';
        this.attendanceToDate = rows[rows.length - 1].date || '';
      }
      const normalizedRows = rows
        .map((row, idx) => ({
          id: row.date || `r-${idx}`,
          dayNote: row.dayDescription || '',
          workDate: row.date || '',
          dayName: this.getDayNameShort(row.date || ''),
          punchInTime: this.formatTime12Hour(row.punchInTime || ''),
          punchOutTime: this.formatTime12Hour(row.punchOutTime || ''),
          dayType: row.dayType || '',
          attendanceFraction: this.formatDayFraction(row.attendanceFraction),
          leaveFraction: this.formatDayFraction(row.leaveFraction),
          holidayFraction: this.formatDayFraction(row.holidayFraction),
          payableFraction: this.formatDayFraction(row.payableFraction),
          lateTier: this.formatLateDeductionDisplay(row),
          lateMinutes: this.formatWholeNumberOrZero(row.lateMinutes),
          latePermission: row.latePermission || '-',
          earlyLeavePermission: row.earlyLeavePermission || '-',
          duration: this.formatAmountOrZero(row.durationHours),
          punchInNote: row.punchInNote || '',
          punchOutNote: row.punchOutNote || '',
        }))
        .sort((a, b) => String(a.workDate).localeCompare(String(b.workDate)));
      this.attendanceRecords = [
        ...normalizedRows,
        {
          id: 'total-row',
          dayNote: '',
          workDate: '',
          dayName: '',
          punchInTime: '',
          punchOutTime: '',
          dayType: 'TOTAL',
          attendanceFraction: this.formatDayFraction(totals.attendanceFraction),
          leaveFraction: this.formatDayFraction(totals.leaveFraction),
          holidayFraction: this.formatDayFraction(totals.holidayFraction),
          payableFraction: this.formatDayFraction(totals.payableFraction),
          lateTier: this.formatLateDeductionTotalDisplay(totals),
          lateMinutes: this.formatWholeNumberOrZero(totals.lateMinutes),
          latePermission: '',
          earlyLeavePermission: '',
          duration: this.formatAmountOrZero(totals.durationHours),
          punchInNote: '',
          punchOutNote: '',
        },
      ];
      this.calculation = {
        ...emptyCalculation(),
        basicSalaryMonthly: this.formatCalculationValue(
          summary.basicSalaryMonthly,
        ),
        basicSalary: this.formatCalculationValue(summary.basicSalaryDailyRate),
        grossSalary: this.formatCalculationValue(summary.grossSalary),
        scheduledWorkDays: this.formatCalculationValue(
          summary.scheduledWorkDays,
        ),
        publicHolidays: this.formatCalculationValue(
          summary.publicHolidays ?? summary.holidayFraction,
        ),
        leaveFraction: this.formatCalculationValue(summary.leaveFraction),
        holidayFraction: this.formatCalculationValue(
          summary.holidayFraction ?? summary.publicHolidays,
        ),
        totalCalculationDays: this.formatCalculationValue(
          summary.totalCalculationDays,
        ),
        payableDays: this.formatCalculationValue(totals.payableFraction),
        workingDays: this.formatCalculationValue(summary.attendedDays),
        lateMinutesTotal: this.formatWholeNumberOrZero(
          summary.lateMinutesTotal,
        ),
        earlyDepartures: this.formatCalculationValue(summary.earlyDepartures),
        absenceDays: this.formatCalculationValue(summary.absenceDays),
        taxableIncome: this.formatCalculationValue(summary.taxableIncome),
      };
    },
    formatCalculationValue(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '';
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    formatAmountOrZero(amt) {
      const n = Number(amt);
      if (!Number.isFinite(n)) return '0';
      return this.formatCalculationValue(n) || '0';
    },
    formatLateDeductionDisplay(row) {
      if (!row || typeof row !== 'object') {
        return '0';
      }
      const classification = String(row.lateDeductionClassification || '');
      const amt = Number(row.lateDeduction);
      const hasAmt = Number.isFinite(amt) && Math.abs(amt) >= 0.005;

      if (
        classification === 'half_day' ||
        classification === 'permission_waived' ||
        !hasAmt
      ) {
        return '0';
      }
      if (classification === 'day_fraction') {
        const frac = Number(row.lateDeductionDayFraction);
        const fracText =
          Number.isFinite(frac) && frac > 0
            ? `${this.formatCalculationValue(frac)} day(s)`
            : '';
        const amtText = this.formatAmountOrZero(amt);
        return fracText ? `${fracText} ${amtText}` : amtText;
      }
      if (classification === 'amount') {
        return this.formatAmountOrZero(amt);
      }
      if (classification === 'percent') {
        return this.formatLateDeductionWithPercent(amt, row.matchedBandPercent);
      }
      const pct = Number(row.matchedBandPercent);
      if (Number.isFinite(pct) && pct > 0) {
        return this.formatLateDeductionWithPercent(amt, pct);
      }
      return this.formatAmountOrZero(amt);
    },
    formatLateDeductionTotalDisplay(totals) {
      if (!totals || typeof totals !== 'object') {
        return '0';
      }
      const pctTotal = Number(totals.latePercentPointsTotal) || 0;
      const pctAmount = Number(totals.latePercentDeduction) || 0;
      if (pctTotal > 0 && pctAmount > 0) {
        return this.formatLateDeductionWithPercent(pctAmount, pctTotal);
      }
      return this.formatAmountOrZero(Number(totals.lateDeduction) || 0);
    },
    formatLatePercentBandLabel(percent) {
      const n = Number(percent);
      if (!Number.isFinite(n) || n <= 0) {
        return '';
      }
      return `${n.toFixed(2).replace(/\.?0+$/, '')}%`;
    },
    formatLateDeductionWithPercent(amount, percent) {
      const amt = Number(amount);
      const pct = Number(percent);
      const hasAmt = Number.isFinite(amt) && Math.abs(amt) >= 0.005;
      const hasPct = Number.isFinite(pct) && pct > 0;
      if (!hasAmt && !hasPct) {
        return '0';
      }
      if (hasAmt && !hasPct) {
        return this.formatAmountOrZero(amt);
      }
      const pctLabel = this.formatLatePercentBandLabel(pct);
      const pctText = pctLabel ? `(${pctLabel})` : '';
      const amtText = amt.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `${pctText} ${amtText}`;
    },
    formatWholeNumberOrZero(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '0';
      return Math.round(n).toLocaleString();
    },
    formatDayFraction(value) {
      const n = Number(value);
      if (!Number.isFinite(n) || n <= 0) return '0';
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    getDayNameShort(dateStr) {
      const d = moment(dateStr, 'YYYY-MM-DD', true);
      return d.isValid() ? d.format('ddd').toUpperCase() : '';
    },
    formatTime12Hour(time) {
      if (!time || typeof time !== 'string') {
        return '';
      }
      const parsed = moment(
        time,
        [
          'YYYY-MM-DD HH:mm:ss',
          'YYYY-MM-DD H:mm:ss',
          'YYYY-MM-DD HH:mm',
          'YYYY-MM-DD H:mm',
          'HH:mm:ss',
          'H:mm:ss',
          'HH:mm',
          'H:mm',
          'hh:mm:ss A',
          'h:mm:ss A',
          'hh:mm A',
          'h:mm A',
        ],
        true,
      );
      if (parsed.isValid()) {
        return parsed.format('hh:mm A');
      }
      const fallback = moment(time);
      return fallback.isValid() ? fallback.format('hh:mm A') : time;
    },
  },
};
</script>

<style lang="scss" scoped>
// Class is applied to oxd-dialog-sheet (dialog root), not a wrapper.
.my-payslip-details-dialog {
  box-sizing: border-box;
  max-width: 100%;
  overflow-x: clip;

  @media (max-width: 767px) {
    min-height: 100dvh;
    height: auto;
    background-color: $oxd-white-color;

    ::v-deep(.oxd-dialog-close-button-position) {
      position: absolute;
      top: 0.75rem;
      right: 0.75rem;
      z-index: 2;
    }
  }
}

.my-payslip-details {
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 1rem;

  @media (max-width: 767px) {
    padding-top: 2.75rem;
  }

  @include oxd-respond-to('md') {
    padding: 0;
  }
}

.my-payslip-details-dialog {
  ::v-deep(.oxd-form),
  ::v-deep(.oxd-form-row),
  ::v-deep(.oxd-grid),
  ::v-deep(.oxd-grid-item),
  ::v-deep(.oxd-input-group),
  ::v-deep(.oxd-input-field-bottom-space) {
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }
}

.attendance-row .orangehrm-container {
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.my-payslip-details__title {
  margin-bottom: 0.5rem;
}

.title-payslip-section {
  width: 100%;
  margin: 0.75rem 0 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #d9dee9;
}

.attendance-section-title .attendance-record-count {
  color: #64748b;
  font-weight: 400;
}

.attendance-legend-note {
  margin-top: 0.5rem;
  color: #64748b;
  font-size: 0.85rem;
}

.attendance-row {
  margin-bottom: 1.5rem;
  min-width: 0;
}

.payslip-details-fields-row {
  min-width: 0;
  margin-bottom: 1rem;
}

.payslip-items-tables {
  width: 100%;
  min-width: 0;

  ::v-deep(.payslip-items-table-scroll) {
    width: 100%;
  }
}

.payslip-items-row {
  min-width: 0;
  margin-bottom: 1rem;
}
</style>

<!-- Overlay/container are outside scoped sheet; target them when this dialog is open. -->
<style lang="scss">
@media (max-width: 767px) {
  body.my-payslip-details-dialog-open {
    overflow: hidden !important;
    overflow-x: hidden !important;
  }

  body.my-payslip-details-dialog-open .oxd-overlay--flex-centered {
    align-items: stretch !important;
    justify-content: flex-start !important;
    padding: 0 !important;
  }

  body.my-payslip-details-dialog-open .oxd-dialog-container-default {
    width: 100% !important;
    max-width: 100% !important;
    height: 100dvh !important;
    max-height: 100dvh !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    display: block !important;
    box-sizing: border-box !important;
  }

  body.my-payslip-details-dialog-open .oxd-dialog-container-default--inner {
    width: 100% !important;
    max-width: 100% !important;
    min-height: 100% !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    display: block !important;
    box-sizing: border-box !important;
  }

  body.my-payslip-details-dialog-open
    .my-payslip-details-dialog.oxd-dialog-sheet {
    min-height: 100dvh !important;
    height: auto !important;
    background-color: #fff !important;
  }

  body.my-payslip-details-dialog-open .oxd-dialog-close-button-position {
    position: absolute !important;
    top: 0.75rem !important;
    right: 0.75rem !important;
    z-index: 500 !important;
  }
}
</style>
